import React, { useContext, useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'

import { Button, Modal, TextField } from '@mui/material'

import useRole from '../../useUserRole'
import { RowsContext } from '../../pages/TablePage'
import { SelectFilterInput } from '../filters/inputs'
import dataTableColumns from '../../config/dataTableColumns.json'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import reportApi from '../../api/table'
import { setSystemMessage } from '../../features/systemMessageSlice'
import {
  RowDetailButton,
  RowDetailButtonGroup,
  RowDetailCard,
  RowDetailCardContent,
  RowDetailCloseButton,
  RowDetailItemTypeTypography,
  RowDetailItemTypography,
  RowDetailMainItemTypeTypography,
  RowDetailMainItemTypography,
  RowDetailModalItem,
  RowDetailModalMainItem,
  rowDetailModalStyleMainItem,
} from '../../styles/components/RowDetailModalStyle'
import theme from '../../theme'

const tableColumns = dataTableColumns.tableColumns

const saveTemplateAsFile = (filename, dataObjToWrite) => {
  const blob = new Blob([JSON.stringify(dataObjToWrite, null, 2)], {
    type: 'text/json',
  })
  const link = document.createElement('a')

  link.download = filename
  link.href = window.URL.createObjectURL(blob)
  link.dataset.downloadurl = ['text/json', link.download, link.href].join(':')

  const e = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  })

  link.dispatchEvent(e)
  link.remove()
}

const RowDetailModal = ({ clickedRowIndex, isModalOpen, handleModal, sequenceId, setUpdatedItemIndex }) => {
  const { selectedRows, setRows, rows } = useContext(RowsContext)
  const { userRole } = useRole()
  const isAdmin = userRole === 'admin'

  const targetRow = rows[clickedRowIndex]

  const [inputData, setInputData] = useState(targetRow || {})
  const [edited, setEdited] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const isSelectedAndSequence = sequenceId && selectedRows.size > 0
  const dispatch = useDispatch()

  const handleDialog = () => {
    setIsDialogOpen(!isDialogOpen)
  }

  useEffect(() => {
    const updateSequence = async () => {
      if (!inputData.steps && sequenceId) {
        const sequence_response = await reportApi.getSequence(sequenceId)
        if (sequence_response.status === 200) {
          setInputData({
            ...inputData,
            steps: sequence_response.data.steps.map((step, idx) => {
              return {
                name: step.name,
                duration: inputData.sequence_results[idx].duration || 'N/A',
                result: inputData.sequence_results[idx].result,
                configuration: step.configuration,
              }
            }),
          })
        }
      }
    }
    updateSequence()
  }, [])

  const handleError = (error) => {
    let msg = null
    let sev = null
    if (error.response) {
      if ('ERR' in error.response.data) {
        msg = error.response.data['ERR']
        sev = 'error'
      } else {
        msg = 'Invalid input data'
        sev = 'error'
      }
    } else if (error.request) {
      msg = 'Server is not responding, please try again later'
      sev = 'error'
    } else {
      msg = error.message
      sev = 'error'
    }
    dispatch(setSystemMessage({ message: msg, severity: sev }))
  }

  const sequenceDataRename = (data, reverse = false) => {
    const copyUpdatedData = { ...data }

    if (reverse) {
      copyUpdatedData.sequence_description = copyUpdatedData.description
      copyUpdatedData.sequence_name = copyUpdatedData.name
      copyUpdatedData.sequence_invoice = copyUpdatedData.project
      copyUpdatedData.login = copyUpdatedData.require_login

      delete copyUpdatedData.description
      delete copyUpdatedData.name
      delete copyUpdatedData.project
      delete copyUpdatedData.require_login
    } else {
      copyUpdatedData.description = copyUpdatedData.sequence_description
      copyUpdatedData.name = copyUpdatedData.sequence_name
      copyUpdatedData.project = copyUpdatedData.sequence_invoice
      copyUpdatedData.user = copyUpdatedData.user?.username
    }

    return copyUpdatedData
  }

  /* Determine if should be updated report or sequence and PUT formated values */
  const updateRow = async (updatedRowData, isSequence = false) => {
    const rowId = rows[clickedRowIndex]?.id

    const copyUpdatedData = sequenceDataRename(updatedRowData)

    if (isSequence) {
      copyUpdatedData.sequence = copyUpdatedData.sequence_id
      reportApi.updateReport(updatedRowData.id, copyUpdatedData)
    }

    const request = isSequence
      ? reportApi.updateSequence(copyUpdatedData.sequence_id, copyUpdatedData)
      : reportApi.updateReport(rowId, copyUpdatedData)
    return await request.then((data) => data.data).catch((error) => handleError(error))
  }

  /* Create new sequence with newly generated ID */
  const createSequence = async () => {
    const newSequenceData = sequenceDataRename(inputData)

    delete newSequenceData.sequence_id

    return await reportApi
      .createSequence([newSequenceData])
      .then((data) => data.data)
      .catch((error) => handleError(error))
  }

  /* Assign sequence to target rows */
  const assignSequence = (newSequence) => {
    let newRows = [...rows]
    const rowIndexes = []
    /* Firstly updated data on backed, then updates fetched rows and at last return affected rows  */
    selectedRows.forEach(async (rowId) => {
      const rowIndex = rows.indexOf(rows.find((row) => row.id === rowId))
      let newSequenceData = { ...rows[rowIndex], ...newSequence[0] }
      newSequenceData = sequenceDataRename(newSequenceData, true)
      updateRow(newSequenceData, true)
      newRows = updateRowArray(rowIndex, newSequenceData, undefined, newRows)
      rowIndexes.push(rowIndex)
    })

    setRows(newRows)
    return rowIndexes
  }

  /* Returns indexes of rows to rerender */
  const getAffectedRows = (selectedRow) => {
    if (isSelectedAndSequence) {
      return selectedRows
    }

    if (!sequenceId) {
      return [clickedRowIndex]
    }

    return rows.flatMap((row, index) => (row.sequence_id === selectedRow.sequence_id ? index : []))
  }

  /* Find and update column value in array of rows  */
  const updateRowArray = (rowIndex, newData, affectedRows, rowsData = rows) => {
    const updatedRowData = [...rowsData]

    if (typeof rowIndex === 'number') {
      updatedRowData[rowIndex] = newData
    } else {
      affectedRows.forEach((rowIndex) => {
        updatedRowData[rowIndex] = newData
      })
    }

    return updatedRowData
  }

  const handleSequenceDownload = async () => {
    await reportApi
      .downloadSequence(sequenceId)
      .then((data) => saveTemplateAsFile(`export_sequence_${sequenceId}.json`, data.data))
      .catch(handleError)
  }

  /* Function to update clicked or selected rows with edit data */
  const handleEditSelected = async () => {
    handleModal()
    handleDialog()

    /* Selected multiple rows with checkbox */
    if (isSelectedAndSequence) {
      const createdSequence = await createSequence()
      const affectedRowsIndexes = assignSequence(createdSequence)
      setUpdatedItemIndex(affectedRowsIndexes)
      /* Clicked column */
    } else {
      updateRow(inputData, sequenceId && true)
      const affectedRows = getAffectedRows(rows[clickedRowIndex])
      const updatedRowData = updateRowArray(undefined, inputData, affectedRows)
      setRows(updatedRowData)
      setUpdatedItemIndex(affectedRows)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    const newData = {
      ...inputData,
      [name]: value,
    }

    setInputData(newData)

    setEdited(JSON.stringify(rows[clickedRowIndex] !== JSON.stringify(newData)))
  }

  const filterDataCondition = (column) => (sequenceId ? column.isSequenceAttribute : column.isInDetail)

  const modalClickedEditableRender = []
  const modalSequenceDisplayOnlyClickedRender = tableColumns
    .filter((column) => filterDataCondition(column))
    .map((column, index) => {
      const fieldValue = inputData[column.field]

      if (column.isEditable && isAdmin) {
        if (column.options.length !== 0) {
          modalClickedEditableRender.push(
            <SelectFilterInput
              key={index}
              sx={{
                flexGrow: '1',
                flexDirection: 'column',
                display: 'flex',
                minWidth: '48%',
                margin: '0.5rem 1%',
                [theme.breakpoints.down('sm')]: {
                  minWidth: '98%',
                },
              }}
              filter={column}
              selectedValues={inputData}
              handleSelectedValues={handleChange}
              options={column.options}
              hasNullValue={false}
            />
          )
          return []
        }

        modalClickedEditableRender.push(
          <RowDetailModalItem key={index}>
            <TextField
              multiline
              maxRows={4}
              name={column.field}
              value={fieldValue}
              onChange={handleChange}
              variant="outlined"
              label={column.headerName}
            />
          </RowDetailModalItem>
        )
        return []
      }

      let displayValue = fieldValue || typeof fieldValue === 'boolean' ? fieldValue.toString() : ''
      /* Key is user object */
      if (fieldValue?.username) displayValue = fieldValue.username
      /* Takes flashed boolean value and translates it to english   */
      if (column.field === 'flashed') displayValue = fieldValue ? 'YES' : 'NO'
      if (column.field === 'flashed' && fieldValue === null) displayValue = ''

      return (
        <RowDetailItemTypography key={index} variant="h6">
          <RowDetailItemTypeTypography variant="subtitle1" component="span">
            {column.headerName}:
          </RowDetailItemTypeTypography>
          {displayValue}
        </RowDetailItemTypography>
      )
    })

  return (
    <Modal
      sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}
      open={isModalOpen}
      onClose={handleModal}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <RowDetailCard elevation={2}>
        <RowDetailCardContent>
          {isSelectedAndSequence ? (
            <RowDetailMainItemTypography variant="h4">{'New sequence'}</RowDetailMainItemTypography>
          ) : (
            <RowDetailMainItemTypography variant="h4">
              <RowDetailMainItemTypeTypography variant="subtitle1" component="span">
                Flash sequence ID:
              </RowDetailMainItemTypeTypography>
              {inputData.sequence_id}
            </RowDetailMainItemTypography>
          )}

          {modalSequenceDisplayOnlyClickedRender}
          {modalClickedEditableRender}
          {sequenceId && (
            <RowDetailModalMainItem>
              <TextField
                multiline
                name="sequenceSteps"
                value={JSON.stringify(inputData.steps, null, 4)}
                onChange={handleChange}
                variant="outlined"
                label="Steps"
                sx={rowDetailModalStyleMainItem}
                slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
              />
            </RowDetailModalMainItem>
          )}
        </RowDetailCardContent>

        <RowDetailButtonGroup>
          <RowDetailButton variant="contained" color="secondary" disabled={!edited} onClick={handleDialog}>
            {isSelectedAndSequence ? 'Save as new sequence' : 'Save changes'}
          </RowDetailButton>
          {sequenceId && (
            <RowDetailButton variant="contained" onClick={handleSequenceDownload} disabled={!isAdmin}>
              Download sequence
            </RowDetailButton>
          )}
        </RowDetailButtonGroup>

        {isDialogOpen && (
          <ConfirmDialog
            isDialogOpen={isDialogOpen}
            handleDialog={handleDialog}
            title={`Do you want to ${isSelectedAndSequence ? 'save changes as new sequence' : 'save changes'}?`}
          >
            <Button onClick={handleDialog} color="default">
              No
            </Button>
            <Button onClick={handleEditSelected} color="primary" autoFocus>
              Yes
            </Button>
          </ConfirmDialog>
        )}

        <RowDetailCloseButton onClick={handleModal} />
      </RowDetailCard>
    </Modal>
  )
}

RowDetailModal.propTypes = {
  clickedRowIndex: PropTypes.number,
  isModalOpen: PropTypes.bool,
  handleModal: PropTypes.func,
  sequenceId: PropTypes.string,
  setUpdatedItemIndex: PropTypes.func,
}

export default RowDetailModal
